import { type FC, useState } from "react";

import { CourseSelectionList } from "./CourseSelectionList";
import { MoveCoursesConfirmationModal } from "./MoveCoursesConfirmationModal";

import { serverAddCoursesToGrouping } from "api/api-server-actions";
import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";
import { selectCoursesWithGrouping } from "store/selectors/section";
import { useListSelector } from "store/store-hooks";
import { toastSuccessMessage } from "utils/alerts";

type MoveCoursesToGroupingModalProps = {
  groupingId: string;
  onClose: () => void;
};

export const MoveCoursesToGroupingModal: FC<MoveCoursesToGroupingModalProps> = ({
  groupingId,
  onClose,
}) => {
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const coursesWithGrouping = useListSelector(selectCoursesWithGrouping, [
    "id",
    "code",
    "grouping_id",
    "groupingTitle",
  ]);
  const filteredCourses = coursesWithGrouping.filter((course) => course.grouping_id !== groupingId);

  const toggleCourseChecked = (courseId: string) => {
    const newSelectedIds = [...selectedCourseIds];

    const codeIndex = newSelectedIds.indexOf(courseId);
    if (codeIndex > -1) {
      newSelectedIds.splice(codeIndex, 1);
    } else {
      newSelectedIds.push(courseId);
    }

    setSelectedCourseIds(newSelectedIds);
  };

  const handleSubmit = () => {
    if (selectedCourseIds.length === 0) {
      return;
    }

    // Check if any selected course codes belong to a different grouping
    const selectedCourseIdSet = new Set(selectedCourseIds);
    const coursesFromOtherGroupings = filteredCourses.filter(
      (course) =>
        selectedCourseIdSet.has(course.id) &&
        course.grouping_id !== null &&
        course.grouping_id !== groupingId,
    );

    if (coursesFromOtherGroupings.length > 0) {
      // Show confirmation modal
      setShowConfirmation(true);
      return;
    }

    // No course codes from other groupings, proceed directly
    handleMoveCourses();
  };

  const handleMoveCourses = async () => {
    await serverAddCoursesToGrouping(groupingId, selectedCourseIds);
    toastSuccessMessage(t("home.toasts.course_moved", { count: selectedCourseIds.length }));
    onClose();
  };

  const handleConfirmationCancel = () => {
    setShowConfirmation(false);
  };

  const handleConfirmationConfirm = async () => {
    await handleMoveCourses();
  };

  if (showConfirmation) {
    const selectedCourseIdSet = new Set(selectedCourseIds);
    const coursesFromOtherGroupings = filteredCourses.filter(
      (course) =>
        selectedCourseIdSet.has(course.id) &&
        course.grouping_id !== null &&
        course.grouping_id !== groupingId,
    );

    return (
      <MoveCoursesConfirmationModal
        courses={coursesFromOtherGroupings}
        onCancel={handleConfirmationCancel}
        onConfirm={handleConfirmationConfirm}
      />
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-2">
        <CourseSelectionList
          courses={filteredCourses}
          selectedCourseIds={selectedCourseIds}
          onCourseToggle={toggleCourseChecked}
        />
      </div>

      <div className="mt-8 flex w-full justify-end gap-2">
        <SquareButton kind="tertiary" size="m" title={t("common.cancel")} onClick={onClose}>
          {t("common.cancel")}
        </SquareButton>

        <SquareButton
          disabled={selectedCourseIds.length === 0}
          kind="accent"
          size="m"
          title={t("home.courses.move_selected_courses")}
          onClick={handleSubmit}
        >
          {t("home.courses.move_selected_courses")}
        </SquareButton>
      </div>
    </div>
  );
};
